import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, ArrowsClockwise, DownloadSimple, MagnifyingGlass, CalendarDots, X, Sparkle, Megaphone, Trash } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import MarketingWizard from "@/components/marketing/MarketingWizard";

const STATUS_COLORS = {
  "Draft": "bg-slate-100 text-slate-700",
  "In Review / Author": "bg-amber-100 text-amber-800",
  "In Review / Marketing": "bg-sky-100 text-sky-800",
  "On Option": "bg-purple-100 text-purple-800",
  "Published": "bg-emerald-100 text-emerald-800",
  "Rejected": "bg-rose-100 text-rose-800",
  "Active": "bg-blue-100 text-blue-800",
  "Ended": "bg-slate-200 text-slate-600",
};
const CHART_PALETTE = ["#002FA7", "#0EA5E9", "#8B5CF6", "#F59E0B", "#10B981", "#EF4444", "#64748B"];

export default function Marketing() {
  const { user } = useAuth();
  const [tab, setTab] = useState("list");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [choices, setChoices] = useState(null);
  const [presets, setPresets] = useState(null);
  const [dateRange, setDateRange] = useState({ from: "", to: "", label: "" });
  // Independent Year + Quarter selection — decoupled from `dateRange` so the
  // dropdowns never fight each other. Selecting a year alone applies the full
  // year range; picking a quarter narrows it. "All" resets that half only.
  const [selYear, setSelYear] = useState("all");
  const [selQuarter, setSelQuarter] = useState("all");
  const [classification, setClassification] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(null); // {mode, ids, label}
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user?.role === "admin";

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allSelected ? [] : items.map((m) => m.id));

  const runDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      if (confirm.mode === "bulk") {
        await api.post("/marketing/items/bulk-delete", { ids: confirm.ids });
      } else {
        await api.delete(`/marketing/items/${confirm.ids[0]}`);
      }
      toast.success(`Deleted ${confirm.ids.length} item${confirm.ids.length > 1 ? "s" : ""}`);
      setSelected([]);
      setConfirm(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (classification !== "all") params.classification = classification;
      if (statusFilter !== "all") params.current_status = statusFilter;
      if (q.trim()) params.q = q.trim();
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      const [it, ch, gen] = await Promise.all([
        api.get("/marketing/items", { params }),
        api.get("/marketing/choices"),
        presets ? Promise.resolve({ data: { quarter_presets: presets } }) : api.get("/settings/general"),
      ]);
      setItems(it.data || []);
      setChoices(ch.data);
      if (!presets) setPresets(gen.data?.quarter_presets || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load marketing items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [classification, statusFilter, dateRange]);
  useEffect(() => {
    const t = setTimeout(fetchAll, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  useEffect(() => {
    if (tab !== "analytics") return;
    const params = {};
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    api.get("/marketing/analytics", { params })
      .then((r) => setAnalytics(r.data))
      .catch(() => toast.error("Could not load analytics"));
  }, [tab, dateRange]);

  const clearDates = () => {
    setSelYear("all"); setSelQuarter("all");
    setDateRange({ from: "", to: "", label: "" });
  };

  // Whenever Year or Quarter changes, compute the effective date window
  // from the server-supplied presets. This runs once per user selection —
  // no chip highlighting mid-render, so the UI never "jumps around".
  useEffect(() => {
    if (!presets?.fiscal_years) return;
    if (selYear === "all") {
      setDateRange({ from: "", to: "", label: "" });
      return;
    }
    const yr = presets.fiscal_years.find((y) => y.key === selYear);
    if (!yr) return;
    if (selQuarter === "all") {
      setDateRange({ from: yr.start, to: yr.end, label: yr.label });
      return;
    }
    const qtr = (yr.quarters || []).find((q) => q.key === selQuarter);
    if (qtr) setDateRange({ from: qtr.start, to: qtr.end, label: `${yr.label} · ${qtr.key}` });
  }, [selYear, selQuarter, presets]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/marketing/sync-sharepoint");
      toast.success(`SharePoint sync: ${data.imported} new, ${data.updated} updated (${data.total_rows} rows)`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SharePoint sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const downloadCsv = () => {
    const p = new URLSearchParams();
    if (dateRange.from) p.set("date_from", dateRange.from);
    if (dateRange.to) p.set("date_to", dateRange.to);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/marketing/export.csv?${p.toString()}`;
    window.open(url, "_blank");
  };

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <MarketingWizard
        open={showWizard}
        onOpenChange={setShowWizard}
        choices={choices}
        onCreated={() => { setShowWizard(false); fetchAll(); }}
      />

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-tracking mb-1 inline-flex items-center gap-1">
            <Megaphone size={12} /> Content operations
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-slate-900 tracking-tight leading-none">Marketing.</h1>
          <p className="text-sm text-slate-500 mt-3 max-w-xl">Track articles, campaigns, LinkedIn carousels, podcasts and videos through review and publication.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button data-testid="marketing-sync-btn" variant="outline" onClick={runSync} disabled={syncing}
              className="rounded-sm border-slate-300 gap-2 hover:bg-slate-900 hover:text-white hover:border-slate-900">
              <ArrowsClockwise size={16} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing…" : "Resync SharePoint"}
            </Button>
          )}
          <Button data-testid="marketing-export-btn" variant="outline" onClick={downloadCsv}
            className="rounded-sm border-slate-300 gap-2 hover:bg-slate-900 hover:text-white hover:border-slate-900">
            <DownloadSimple size={16} /> Export CSV
          </Button>
          {isAdmin && (
            <Button data-testid="marketing-add-btn" onClick={() => setShowWizard(true)}
              className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2">
              <Plus size={16} /> Add item
            </Button>
          )}
        </div>
      </div>

      {/* Year + Quarter filter — two independent dropdowns so selection never "jumps" */}
      {presets && (
        <div className="flex flex-wrap items-center gap-2" data-testid="marketing-quarter-presets">
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 mr-1">
            <CalendarDots size={12} /> Date filter:
          </span>
          <Select value={selYear} onValueChange={setSelYear}>
            <SelectTrigger data-testid="marketing-year-select" className="h-8 rounded-sm text-xs w-36 border-slate-300">
              <SelectValue placeholder="All years"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All years</SelectItem>
              {(presets.fiscal_years || []).map((y) => (
                <SelectItem key={y.key} value={y.key}>
                  {y.label}{y.is_current ? " · current" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selQuarter} onValueChange={setSelQuarter} disabled={selYear === "all"}>
            <SelectTrigger data-testid="marketing-quarter-select" className="h-8 rounded-sm text-xs w-32 border-slate-300 disabled:opacity-50">
              <SelectValue placeholder="All quarters"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All quarters</SelectItem>
              <SelectItem value="Q1">Q1</SelectItem>
              <SelectItem value="Q2">Q2</SelectItem>
              <SelectItem value="Q3">Q3</SelectItem>
              <SelectItem value="Q4">Q4</SelectItem>
            </SelectContent>
          </Select>
          {/* Rolling shortcuts — set the two dropdowns rather than the date range directly */}
          {presets.this_quarter && (
            <button
              type="button"
              data-testid="marketing-preset-this-quarter"
              onClick={() => { setSelYear(presets.this_fiscal_year?.key || "all"); setSelQuarter(presets.this_quarter.key); }}
              className="px-2 py-0.5 rounded-sm text-[11px] border border-slate-200 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
            >
              This quarter
            </button>
          )}
          {presets.last_quarter && (
            <button
              type="button"
              data-testid="marketing-preset-last-quarter"
              onClick={() => {
                // Last quarter may live in the previous FY (when idx == 0).
                const lqYear = (presets.fiscal_years || []).find((y) => presets.last_quarter.start >= y.start && presets.last_quarter.start <= y.end);
                setSelYear(lqYear?.key || presets.this_fiscal_year?.key || "all");
                setSelQuarter(presets.last_quarter.key);
              }}
              className="px-2 py-0.5 rounded-sm text-[11px] border border-slate-200 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
            >
              Last quarter
            </button>
          )}
          {(selYear !== "all" || selQuarter !== "all") && (
            <button type="button" data-testid="marketing-preset-clear" onClick={clearDates}
              className="ml-1 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900">
              <X size={11} /> Clear
            </button>
          )}
          {dateRange.label && (
            <span className="ml-1 text-[11px] text-slate-500">→ {dateRange.label}</span>
          )}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none h-auto p-0 gap-6 justify-start" data-testid="marketing-tabs">
          <TabsTrigger value="list" data-testid="marketing-tab-list" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 pb-3 text-sm">Items ({items.length})</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="marketing-tab-analytics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 pb-3 text-sm">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="marketing-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, description, author…"
                className="pl-9 rounded-sm h-9 text-sm border-slate-300"/>
            </div>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger data-testid="marketing-filter-classification" className="w-[180px] h-9 rounded-sm text-sm border-slate-300">
                <SelectValue placeholder="Classification"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classifications</SelectItem>
                {(choices?.classifications || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="marketing-filter-status" className="w-[200px] h-9 rounded-sm text-sm border-slate-300">
                <SelectValue placeholder="Status"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(choices?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isAdmin && selected.length > 0 && (
            <div className="flex items-center gap-3 bg-slate-900 text-white rounded-sm px-4 py-2" data-testid="marketing-bulk-bar">
              <span className="text-sm font-medium">{selected.length} selected</span>
              <Button
                data-testid="marketing-bulk-delete-btn"
                size="sm" variant="outline"
                onClick={() => setConfirm({ mode: "bulk", ids: selected, label: `${selected.length} items` })}
                className="rounded-sm gap-1 bg-transparent border-rose-300 text-rose-200 hover:bg-rose-600 hover:text-white hover:border-rose-600 h-8">
                <Trash size={14} /> Delete selected
              </Button>
              <button type="button" onClick={() => setSelected([])} className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1">
                <X size={12} /> Clear
              </button>
            </div>
          )}

          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-slate-200 rounded-sm p-12 text-center">
              <Megaphone size={36} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">No marketing items yet. Click "Add item" to start.</p>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-sm overflow-x-auto" data-testid="marketing-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    {isAdmin && (
                      <th className="py-3 px-4 w-10">
                        <input type="checkbox" data-testid="marketing-select-all" checked={allSelected} onChange={toggleAll} className="cursor-pointer" />
                      </th>
                    )}
                    <th className="py-3 px-4 label-tracking !text-[0.65rem]">Title</th>
                    <th className="py-3 px-4 label-tracking !text-[0.65rem]">Classification</th>
                    <th className="py-3 px-4 label-tracking !text-[0.65rem]">Author</th>
                    <th className="py-3 px-4 label-tracking !text-[0.65rem]">Department</th>
                    <th className="py-3 px-4 label-tracking !text-[0.65rem]">Status</th>
                    <th className="py-3 px-4 label-tracking !text-[0.65rem] text-right">AI</th>
                    <th className="py-3 px-4 label-tracking !text-[0.65rem]">Quarter</th>
                    {isAdmin && <th className="py-3 px-4 label-tracking !text-[0.65rem] text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`marketing-row-${m.id}`}>
                      {isAdmin && (
                        <td className="py-3 px-4">
                          <input type="checkbox" data-testid={`marketing-select-${m.id}`} checked={selected.includes(m.id)} onChange={() => toggleSelect(m.id)} className="cursor-pointer" />
                        </td>
                      )}
                      <td className="py-3 px-4"><Link to={`/marketing/${m.id}`} className="text-slate-900 hover:underline">{m.title}</Link></td>
                      <td className="py-3 px-4 text-slate-700">{m.classification}</td>
                      <td className="py-3 px-4 text-slate-700">{(m.authors && m.authors.length) ? m.authors.map((a) => a.name || a.email).filter(Boolean).join(", ") : (m.author_name || m.author_email || "—")}</td>
                      <td className="py-3 px-4 text-slate-500 text-xs">{m.author_department || "—"}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] font-medium ${STATUS_COLORS[m.current_status] || "bg-slate-100 text-slate-700"}`}>
                          {m.current_status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {m.ai_insight?.score != null ? (
                          <span data-testid={`ai-score-${m.id}`} className="inline-flex items-center gap-1 text-xs font-mono">
                            <Sparkle size={12} weight="fill" className="text-purple-500" /> {m.ai_insight.score}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500 font-mono">{m.fiscal_quarter || "—"}</td>
                      {isAdmin && (
                        <td className="py-3 px-4 text-right">
                          <button
                            type="button"
                            data-testid={`marketing-delete-${m.id}`}
                            onClick={() => setConfirm({ mode: "single", ids: [m.id], label: m.title })}
                            className="text-slate-400 hover:text-rose-600 p-1"
                            title="Delete item">
                            <Trash size={15} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <MarketingAnalytics analytics={analytics} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="marketing-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.mode === "bulk" ? `Delete ${confirm?.ids?.length} marketing items?` : "Delete this marketing item?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.mode === "bulk"
                ? "The selected items will be permanently removed from the tracker. This cannot be undone."
                : `"${confirm?.label}" will be permanently removed from the tracker. This cannot be undone.`}
              {" "}Files already uploaded to OneDrive are not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="marketing-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="marketing-delete-confirm" disabled={deleting} onClick={(e) => { e.preventDefault(); runDelete(); }} className="bg-rose-600 hover:bg-rose-700">
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="label-tracking text-[0.6rem]">{label}</div>
      <div className="font-display text-3xl text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function MarketingAnalytics({ analytics }) {
  if (!analytics) return <p className="text-sm text-slate-500">Loading analytics…</p>;
  const { kpi, by_classification, by_status, by_department, by_week, top_authors, this_quarter } = analytics;
  return (
    <div className="space-y-6" data-testid="marketing-analytics">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="In flight" value={kpi.in_flight} sub="Not yet published"/>
        <KpiCard label={`Published (${this_quarter || "quarter"})`} value={kpi.published_this_quarter}/>
        <KpiCard label="Total published" value={kpi.published}/>
        <KpiCard label="Rejection rate" value={`${kpi.rejection_rate_pct}%`}/>
        <KpiCard label="Avg AI score" value={kpi.avg_ai_score} sub={`from ${kpi.avg_ai_score_sample_size} scored`}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="label-tracking mb-1">By classification</div>
          <h3 className="font-display text-lg text-slate-900 mb-3">Content mix</h3>
          {by_classification.length === 0 ? (
            <p className="text-sm text-slate-500">No data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={by_classification} dataKey="count" nameKey="key" outerRadius={90} label>
                  {by_classification.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]}/>)}
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }}/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="label-tracking mb-1">By department</div>
          <h3 className="font-display text-lg text-slate-900 mb-3">Contributors</h3>
          {by_department.length === 0 ? (
            <p className="text-sm text-slate-500">No data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={by_department} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false}/>
                <XAxis dataKey="key" stroke="#64748B" fontSize={11}/>
                <YAxis stroke="#64748B" fontSize={11} allowDecimals={false}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }}/>
                <Bar dataKey="count" fill="#002FA7" radius={[2, 2, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="label-tracking mb-1">Volume</div>
        <h3 className="font-display text-lg text-slate-900 mb-3">Items created — last 14 weeks</h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={by_week}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false}/>
            <XAxis dataKey="date" stroke="#64748B" fontSize={11}
              tickFormatter={(v) => new Date(v + "T00:00:00Z").toLocaleDateString(undefined, { day: "2-digit", month: "short" })}/>
            <YAxis stroke="#64748B" fontSize={11} allowDecimals={false}/>
            <Tooltip labelFormatter={(v) => `Week of ${new Date(v + "T00:00:00Z").toLocaleDateString(undefined, { day: "2-digit", month: "short" })}`}
              contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }}/>
            <Line type="monotone" dataKey="count" stroke="#002FA7" strokeWidth={2} dot={{ fill: "#002FA7", r: 3 }}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="label-tracking mb-1">Leaderboard</div>
        <h3 className="font-display text-lg text-slate-900 mb-3">Top authors</h3>
        {top_authors.length === 0 ? (
          <p className="text-sm text-slate-500">No data yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-2 label-tracking !text-[0.65rem]">Author</th>
                <th className="py-2 label-tracking !text-[0.65rem] text-right">Items</th>
                <th className="py-2 label-tracking !text-[0.65rem] text-right">Published</th>
                <th className="py-2 label-tracking !text-[0.65rem] text-right">Avg AI score</th>
              </tr>
            </thead>
            <tbody>
              {top_authors.map((a, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 text-slate-900">{a.name}</td>
                  <td className="py-3 text-right font-mono">{a.count}</td>
                  <td className="py-3 text-right font-mono text-emerald-700">{a.published}</td>
                  <td className="py-3 text-right font-mono">{a.avg_score || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
